// ─── IBOS (Integrated Benefits Optimization Strategy) Calculation Engine ───
// Pure functions that convert {workforceTotals, layers config, clientOverrides} → per-layer and aggregate results.

/**
 * Get the effective layer definition by merging:
 *   admin layer config ← per-client overrides
 */
export function mergeLayerWithOverrides(layer, clientOverride) {
  if (!clientOverride) return layer;
  const merged = { ...layer };
  // Allow override of: defaultEnabled, inputs (shallow), defaultStatus
  if (clientOverride.enabled !== undefined) merged.defaultEnabled = clientOverride.enabled;
  if (clientOverride.status !== undefined) merged.defaultStatus = clientOverride.status;
  if (clientOverride.inputs) {
    merged.inputs = { ...(layer.inputs || {}), ...clientOverride.inputs };
  }
  if (clientOverride.employerSavingsParams) {
    merged.employerSavingsParams = {
      ...(layer.employerSavingsParams || {}),
      ...clientOverride.employerSavingsParams,
    };
  }
  if (clientOverride.employeeValueParams) {
    merged.employeeValueParams = {
      ...(layer.employeeValueParams || {}),
      ...clientOverride.employeeValueParams,
    };
  }
  return merged;
}

/**
 * Compute a single layer's annualized financial impact.
 *
 * @param {object} layer - Effective layer config (after merging admin + client overrides)
 * @param {object} context - Shared calculation context
 *   {
 *     workforceTotals: { count, eligible, emprFica, empBenefit, hcRevenue, byPlan },
 *     avgAnnualSalary: number,  // for absenteeism layer
 *     workdaysPerYear: number,  // default 250
 *   }
 * @returns {{employerSavings:number, employeeValue:number, healthcareImpact:number, enrolledCount:number, notes:string[]}}
 */
export function computeLayerImpact(layer, context) {
  const notes = [];
  const totals = context.workforceTotals || {};
  const eligibleCount = Number(totals.eligible || 0);
  const workforceCount = Number(totals.count || 0);

  let employerSavings = 0;
  let employeeValue = 0;
  let healthcareImpact = 0;
  let enrolledCount = eligibleCount;

  const method = layer.calculationMethod || 'custom';
  const inputs = layer.inputs || {};

  switch (method) {
    case 'computed_from_section125': {
      // Layer 1: pulls live from workforce analysis
      employerSavings = Number(totals.emprFica || 0);
      employeeValue = Number(totals.empBenefit || 0);
      healthcareImpact = 0;
      break;
    }

    case 'per_employee_monthly': {
      // Layers 2 (HRA), 4 (MEC), 6 (DPC)
      const monthlyContribution = Number(inputs.monthlyContribution || 0);
      const annualContributionPerEmp = monthlyContribution * 12;
      const employerCostTotal = annualContributionPerEmp * eligibleCount;

      // Employer savings
      const esMethod = layer.employerSavingsMethod || 'none';
      if (esMethod === 'tax_deduction_value') {
        const taxRate = Number(layer.employerSavingsParams?.taxRate ?? 0.272);
        employerSavings = employerCostTotal * taxRate;
      } else if (esMethod === 'cost_avoidance_roi') {
        const roi = Number(layer.employerSavingsParams?.roiMultiplier ?? 1);
        const employerShare = Number(layer.employerSavingsParams?.employerShare ?? 0.3);
        // The ROI returns total savings; employer captures employerShare of it
        employerSavings = employerCostTotal * roi * employerShare;
      } else if (esMethod === 'fixed') {
        employerSavings = Number(layer.employerSavingsParams?.fixedValue ?? 0);
      }

      // Employee value
      const evMethod = layer.employeeValueMethod || 'direct_benefit';
      if (evMethod === 'utilization_adjusted') {
        const utilization = Number(layer.employeeValueParams?.utilizationRate ?? 0.4);
        // Utilizers get the full per-employee annual contribution as a tax-free benefit
        employeeValue = annualContributionPerEmp * Math.round(eligibleCount * utilization);
      } else if (evMethod === 'perceived_value') {
        const perMonth = Number(layer.employeeValueParams?.perceivedValuePerMonth ?? 0);
        employeeValue = perMonth * 12 * eligibleCount;
      } else if (evMethod === 'direct_benefit') {
        employeeValue = employerCostTotal; // equals the employer contribution
      }

      enrolledCount = eligibleCount;
      break;
    }

    case 'utilization_model': {
      // Layer 3 (Telehealth)
      const avoidedEr = Number(inputs.avoidedErVisitsPerYear || 0);
      const avgErCost = Number(inputs.avgErCost || 0);
      const perceivedPerMonth = Number(inputs.perceivedValuePerMonth || 0);

      employerSavings = avoidedEr * avgErCost;
      employeeValue = perceivedPerMonth * 12 * eligibleCount;
      healthcareImpact = avoidedEr * avgErCost * 0.5; // secondary estimate
      break;
    }

    case 'from_plan_tiers': {
      // Layer 5 (Hospitalization) — value already bundled in plan tiers
      const perEnrolledAnnual = Number(inputs.perEnrolledAnnualValue || 0);
      employeeValue = perEnrolledAnnual * eligibleCount;
      employerSavings = 0;
      break;
    }

    case 'rx_spend_model': {
      // Layer 7 (Pharmacy)
      const avgAnnualSpend = Number(inputs.avgAnnualRxSpendPerEmployee || 0);
      const savingsPct = Number(inputs.savingsPercentage || 0);
      const empShare = Number(inputs.employerSharePercentage || 0.2);
      const totalSavings = avgAnnualSpend * savingsPct * workforceCount;
      employerSavings = totalSavings * empShare;
      employeeValue = totalSavings * (1 - empShare);
      break;
    }

    case 'absenteeism_model': {
      // Layer 8 (Wellness)
      const daysReduced = Number(inputs.absenteeismReductionDaysPerEmployee || 0);
      let dailyCost = Number(inputs.avgDailyLaborCost || 0);
      // If avgAnnualSalary available in context, derive more accurate daily cost
      if (context.avgAnnualSalary) {
        const workdays = Number(context.workdaysPerYear || 250);
        dailyCost = context.avgAnnualSalary / workdays;
      }
      employerSavings = daysReduced * dailyCost * workforceCount;
      const perEmpAnnual = Number(layer.employeeValueParams?.perEmployeeAnnualValue || 0);
      employeeValue = perEmpAnnual * workforceCount;
      break;
    }

    default: {
      notes.push(`Unknown calculation method: ${method}`);
      break;
    }
  }

  return {
    employerSavings: Math.max(0, Math.round(employerSavings)),
    employeeValue: Math.max(0, Math.round(employeeValue)),
    healthcareImpact: Math.max(0, Math.round(healthcareImpact)),
    enrolledCount,
    notes,
  };
}

/**
 * Top-level IBOS computation.
 *
 * @param {object} params
 *   {
 *     workforceTotals: { count, eligible, emprFica, empBenefit, hcRevenue, byPlan },
 *     avgAnnualSalary: number,
 *     ibosLayers: Array,           // from config.ibosLayers (admin)
 *     clientIbosConfig: object,    // client.ibosConfig (per-layer overrides)
 *   }
 * @returns {{
 *   layers: Array<{...layer, result, effectiveEnabled}>,
 *   aggregates: { employerSavings, employeeValue, healthcareImpact, total, bySection125, byFullIBOS }
 * }}
 */
export function computeIBOS({
  workforceTotals,
  avgAnnualSalary,
  ibosLayers = [],
  clientIbosConfig = {},
}) {
  const context = {
    workforceTotals: workforceTotals || {},
    avgAnnualSalary: avgAnnualSalary || 0,
    workdaysPerYear: 250,
  };

  const perLayerOverrides = clientIbosConfig.layers || {};

  const results = ibosLayers.map(layer => {
    const override = perLayerOverrides[layer.id];
    const effective = mergeLayerWithOverrides(layer, override);
    const effectiveEnabled = effective.defaultEnabled && effective.defaultStatus !== 'na' && effective.defaultStatus !== 'future';
    const result = computeLayerImpact(effective, context);
    return {
      ...effective,
      rawDefinition: layer,
      override,
      effectiveEnabled,
      effectiveStatus: effective.defaultStatus,
      result,
    };
  });

  // Aggregate only enabled layers
  const aggregate = { employerSavings: 0, employeeValue: 0, healthcareImpact: 0 };
  results.forEach(r => {
    if (r.effectiveEnabled) {
      aggregate.employerSavings += r.result.employerSavings;
      aggregate.employeeValue += r.result.employeeValue;
      aggregate.healthcareImpact += r.result.healthcareImpact;
    }
  });
  aggregate.total = aggregate.employerSavings + aggregate.employeeValue + aggregate.healthcareImpact;

  // Section 125-only totals for comparison view
  const section125 = results.find(r => r.id === 'section125');
  const bySection125 = section125
    ? {
        employerSavings: section125.result.employerSavings,
        employeeValue: section125.result.employeeValue,
        total: section125.result.employerSavings + section125.result.employeeValue,
      }
    : { employerSavings: 0, employeeValue: 0, total: 0 };

  // Full IBOS aggregate assuming ALL layers that are active or available are turned on
  let fullEmpr = 0;
  let fullEmp = 0;
  results.forEach(r => {
    if (r.effectiveStatus === 'active' || r.effectiveStatus === 'available') {
      // Recompute with enabled=true to capture what "full IBOS" would look like
      const temp = computeLayerImpact({ ...r, defaultEnabled: true }, context);
      fullEmpr += temp.employerSavings;
      fullEmp += temp.employeeValue;
    }
  });
  const byFullIBOS = { employerSavings: fullEmpr, employeeValue: fullEmp, total: fullEmpr + fullEmp };

  return {
    layers: results,
    aggregates: {
      ...aggregate,
      bySection125,
      byFullIBOS,
    },
  };
}

/**
 * Toggle a layer's enabled state in a client's ibosConfig.
 */
export function toggleLayerEnabled(clientIbosConfig, layerId, enabled) {
  const next = { ...(clientIbosConfig || {}) };
  next.layers = { ...(next.layers || {}) };
  next.layers[layerId] = { ...(next.layers[layerId] || {}), enabled };
  return next;
}

/**
 * Update a per-client layer input override.
 */
export function updateLayerInput(clientIbosConfig, layerId, inputKey, value) {
  const next = { ...(clientIbosConfig || {}) };
  next.layers = { ...(next.layers || {}) };
  const layer = next.layers[layerId] || {};
  layer.inputs = { ...(layer.inputs || {}), [inputKey]: value };
  next.layers[layerId] = layer;
  return next;
}
